import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { operator, query, DeviceCommands, enqueueOutcome } from '../api';

/**
 * Các hook nối UI với cụm API NanoMDM.
 * - "Ghi/điều khiển"  -> operator.* + DeviceCommands.*  (CỤM 1, chạy được ngay)
 * - "Đọc" (list/info) -> query.*                        (CỤM 2, cần backend query)
 * Khi CỤM 2 chưa cấu hình, tự fallback sang mock data để dev UI.
 */

// ── useDevices ────────────────────────────────────────────────────────────────
export function useDevices(pollInterval = 30000) {
  const [devices, setDevices]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await query.listDevices();
      if (data == null) {
        // Backend query chưa cấu hình -> dùng mock, báo trạng thái cho UI
        setError('Chưa cấu hình backend query (VITE_MDM_QUERY_URL) — đang dùng mock data');
        setDevices(MOCK_DEVICES);
      } else {
        const list = Array.isArray(data) ? data : (data.devices || data.enrollment_ids || []);
        setDevices(list.map(normalizeDevice));
        setLastUpdated(new Date());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (pollInterval > 0) {
      timerRef.current = setInterval(load, pollInterval);
      return () => clearInterval(timerRef.current);
    }
  }, [load, pollInterval]);

  // Optimistic update trong local state
  const updateDevice = useCallback((udid, patch) => {
    setDevices(prev => prev.map(d => d.udid === udid ? { ...d, ...patch } : d));
  }, []);

  const removeDevice = useCallback((udid) => {
    setDevices(prev => prev.filter(d => d.udid !== udid));
  }, []);

  return { devices, loading, error, lastUpdated, reload: load, updateDevice, removeDevice };
}

/** Map dữ liệu backend về shape UI (tolerant với nhiều kiểu key). */
function normalizeDevice(d) {
  if (typeof d === 'string') return { udid: d, id: d, name: d, status: 'active' };
  return {
    id: d.id ?? d.udid ?? d.enrollment_id,
    udid: d.udid ?? d.id ?? d.enrollment_id,
    name: d.name ?? d.device_name ?? d.DeviceName ?? '(chưa đặt tên)',
    serial: d.serial ?? d.SerialNumber ?? '',
    model: d.model ?? d.ModelName ?? d.Model ?? '',
    platform: d.platform ?? 'iOS',
    os: d.os ?? d.OSVersion ?? '',
    build: d.build ?? d.BuildVersion ?? '',
    product: d.product ?? d.ProductName ?? '',
    battery: d.battery ?? d.BatteryLevel ?? 0,
    storage: d.storage ?? '',
    ip: d.ip ?? '',
    mac: d.mac ?? d.WiFiMAC ?? '',
    bt: d.bt ?? d.BluetoothMAC ?? '',
    owner: d.owner ?? '',
    email: d.email ?? '',
    phone: d.phone ?? '',
    status: d.status ?? 'active',
    ...d,
  };
}

// ── useDeviceDetail ───────────────────────────────────────────────────────────
/**
 * Đọc chi tiết 1 thiết bị (info/apps/profiles) từ CỤM 2 khi mở modal.
 * refresh(): enqueue các lệnh truy vấn để YÊU CẦU thiết bị báo cáo (bất đồng bộ),
 *            rồi đọc lại dữ liệu đã lưu. Kết quả mới có thể tới ở lần poll sau.
 */
export function useDeviceDetail(udid) {
  const [info, setInfo]         = useState(null);
  const [apps, setApps]         = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [commands, setCommands] = useState([]);
  const [loading, setLoading]   = useState(false);

  // Chỉ ĐỌC (không đụng tới thiết bị)
  const load = useCallback(async () => {
    if (!udid) return;
    setLoading(true);
    try {
      const [i, a, p, c] = await Promise.allSettled([
        query.getDeviceInfo(udid),
        query.getInstalledApps(udid),
        query.getInstalledProfiles(udid),
        query.getCommandResults(udid, { limit: 50 }),
      ]);
      if (i.status === 'fulfilled' && i.value) setInfo(i.value);
      if (a.status === 'fulfilled' && a.value) setApps(a.value);
      if (p.status === 'fulfilled' && p.value) setProfiles(p.value);
      if (c.status === 'fulfilled' && c.value) setCommands(c.value);
    } catch (err) {
      console.warn('DeviceDetail load failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [udid]);

  // YÊU CẦU thiết bị báo cáo lại rồi đọc — dùng cho nút "Làm mới"/"Check-in"
  const refresh = useCallback(async () => {
    if (!udid) return;
    try {
      await Promise.allSettled([
        DeviceCommands.deviceInformation(udid),
        DeviceCommands.installedApplications(udid),
        DeviceCommands.profileList(udid),
      ]);
    } catch (err) {
      console.warn('DeviceDetail refresh enqueue failed:', err.message);
    }
    await load();
  }, [udid, load]);

  useEffect(() => { load(); }, [load]);

  // reload: đọc lại dữ liệu đã lưu (KHÔNG enqueue lệnh) — dùng để poll cập nhật UI.
  return { info, apps, profiles, commands, loading, refresh, reload: load };
}

// ── useDeviceAction ───────────────────────────────────────────────────────────
/**
 * Dispatcher gửi lệnh MDM. Trả { ok, error } để UI toast.
 */
export function useDeviceAction(updateDevice, removeDevice, customer = false) {
  const [pending, setPending] = useState(null);

  const run = useCallback(async (action, udid, opts = {}) => {
    setPending(udid);
    // CỔNG KHÁCH: ba thao tác này có ENDPOINT RIÊNG (/lock, /unlock, /locate) vì backend
    // còn phải cập nhật device_admin_state chứ không chỉ đẩy lệnh. Riêng chúng nó đi đường
    // này; MỌI THAO TÁC CÒN LẠI rơi xuống dispatcher chung bên dưới — /devices/{id}/push
    // và /devices/{id}/enqueue giờ đã có ở cổng khách (portal_device_ops.go), và backend tự
    // chấm quyền theo vai trò trong chuỗi nên không cần danh sách cho phép riêng ở FE.
    if (customer && ['lock', 'lost_mode_on', 'unlock', 'lost_mode_off', 'locate'].includes(action)) {
      try {
        // "Khoá máy" của khách = Lost Mode (giống "Khoá điện thoại" nội bộ): backend
        // portal /lock enqueue EnableLostMode, /unlock enqueue DisableLostMode.
        if (action === 'lock' || action === 'lost_mode_on') {
          await query.setLocked(udid, opts.message);
          updateDevice(udid, { status: 'locked', locked: true });
          return { ok: true };
        }
        if (action === 'unlock' || action === 'lost_mode_off') {
          await query.setUnlocked(udid);
          updateDevice(udid, { status: 'active', locked: false });
          return { ok: true };
        }
        await query.locateDevice(udid);
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err.message };
      } finally {
        setPending(null);
      }
    }
    try {
      let res;
      switch (action) {
        case 'checkin':
          res = await operator.push(udid);
          break;
        case 'lock':
          res = await DeviceCommands.lock(udid, { message: opts.message, pin: opts.pin });
          break;
        case 'unlock':
          res = await DeviceCommands.clearPasscode(udid);
          break;
        case 'restart':
          res = await DeviceCommands.restart(udid);
          break;
        case 'shutdown':
          res = await DeviceCommands.shutdown(udid);
          break;
        case 'unenroll':
          // Gỡ khỏi MDM (RemoveProfile enrollment) — máy giữ nguyên dữ liệu.
          // KHÔNG removeDevice: DeviceModal poll tới khi máy CheckOut (enabled=false).
          res = await DeviceCommands.unenroll(udid);
          break;
        case 'os_update':
          res = await DeviceCommands.scheduleOSUpdate(udid, opts.version);
          break;
        case 'install_profile':
          res = await DeviceCommands.installProfile(udid, opts.profileBase64);
          break;
        case 'remove_profile':
          res = await DeviceCommands.removeProfile(udid, opts.identifier);
          break;
        case 'install_app':
          res = await DeviceCommands.installApp(udid, { iTunesStoreID: opts.itunesId, manifestURL: opts.manifestUrl });
          break;
        case 'remove_app':
          res = await DeviceCommands.removeApp(udid, opts.identifier);
          break;
        case 'sound':
          res = await DeviceCommands.playSound(udid);
          break;
        case 'wallpaper':
          res = await DeviceCommands.setWallpaper(udid, opts.imageBase64);
          break;
        case 'rename':
          res = await DeviceCommands.setDeviceName(udid, opts.name);
          break;
        case 'lost_mode_on':
          res = await DeviceCommands.enableLostMode(udid, { message: opts.message, phoneNumber: opts.phone });
          break;
        case 'lost_mode_off':
          res = await DeviceCommands.disableLostMode(udid);
          break;
        case 'locate':
          res = await DeviceCommands.deviceLocation(udid);
          break;
        default:
          throw new Error(`Hành động chưa hỗ trợ: ${action}`);
      }

      // NanoMDM trả HTTP 200 KỂ CẢ khi push/enqueue lỗi (lỗi nằm trong APIResult).
      // Soi để báo ĐÚNG cho MỌI lệnh: command_error = thất bại thật; push_error =
      // máy offline (lệnh đã xếp, sẽ tự chạy khi máy online).
      const o = enqueueOutcome(res, udid);
      if (o.failed) return { ok: false, error: o.error || 'Thiết bị từ chối / không xếp được lệnh' };

      // Cập nhật UI lạc quan CHỈ khi lệnh đã xếp OK (kể cả offline — lệnh nằm chờ).
      if (action === 'lock') {
        await query.setLocked(udid, opts.message).catch(() => {}); // bền vững qua reload
        updateDevice(udid, { status: 'locked', locked: true });
      } else if (action === 'unlock') {
        await query.setUnlocked(udid).catch(() => {});
        updateDevice(udid, { status: 'active', locked: false });
      } else if (action === 'rename') {
        updateDevice(udid, { name: opts.name });
      }

      // push_error = APNs từ chối đẩy (token hết hạn / máy đã gỡ MDM / cấu hình push).
      // KHÁC "offline": máy offline mà token còn hợp lệ thì APNs vẫn nhận, không báo lỗi.
      if (o.offline) return { ok: true, warn: 'Đã ghi nhận lệnh nhưng KHÔNG gửi được tới máy — máy có thể đã bị gỡ quản lý hoặc chứng chỉ kết nối đã hết hạn. Lệnh sẽ chạy nếu máy kết nối lại.' };
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err.message };
    } finally {
      setPending(null);
    }
  }, [updateDevice, removeDevice, customer]);

  return { run, pending };
}

// ── useStats ──────────────────────────────────────────────────────────────────
/**
 * Thống kê cho Dashboard/thẻ số liệu. Ưu tiên /stats từ backend query (đếm
 * TOÀN BỘ thiết bị phía server); nếu chưa cấu hình -> tự tính từ mảng devices.
 * @returns {{ total, active, locked, offline, byModel, byOS, byPlatform, source }}
 */
export function useStats(devices) {
  const [server, setServer] = useState(null);

  useEffect(() => {
    let alive = true;
    query.getStats()
      .then(s => { if (alive) setServer(s); })
      .catch(() => { if (alive) setServer(null); });
    return () => { alive = false; };
  }, [devices]); // refetch khi danh sách đổi (reload/poll/khoá-mở)

  return useMemo(() => {
    if (server) {
      return {
        total: server.total,
        active: server.by_status?.active || 0,
        locked: server.by_status?.locked || 0,
        offline: server.by_status?.offline || 0,
        byModel: server.by_model || {},
        byOS: server.by_os || {},
        byPlatform: server.by_platform || {},
        source: 'server',
      };
    }
    const groupBy = (key) => devices.reduce((m, d) => {
      const k = d[key] || '—';
      m[k] = (m[k] || 0) + 1;
      return m;
    }, {});
    return {
      total: devices.length,
      active: devices.filter(d => d.status === 'active').length,
      locked: devices.filter(d => d.status === 'locked').length,
      offline: devices.filter(d => d.status === 'offline').length,
      byModel: groupBy('model'),
      byOS: groupBy('os'),
      byPlatform: groupBy('platform'),
      source: 'client',
    };
  }, [server, devices]);
}

// ── useToast ──────────────────────────────────────────────────────────────────
export function useToast() {
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  const show = useCallback((msg, type = 'success', icon = '✅') => {
    clearTimeout(timerRef.current);
    setToast({ msg, type, icon });
    timerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  return { toast, show };
}

// ── Mock data (dùng khi CỤM 2 chưa cấu hình) ──────────────────────────────────
export const MOCK_DEVICES = [
  { id:1, name:"iPhone Nguyễn Văn A", serial:"F2LF86SJ0D41", udid:"00008101-000024AE269A001E", model:"iPhone 14 Pro", platform:"iOS", os:"17.4.1", build:"21E236", product:"iPhone 14 Pro", battery:85, storage:"45.2 / 128 GB", ip:"192.168.1.101", mac:"a1:b2:c3:d4:e5:f6", bt:"a1:b2:c3:d4:e5:f7", owner:"admin@company.vn", email:"nguyenvana@company.vn", phone:"0901234567", status:"active" },
  { id:2, name:"iPhone Trần Thị B",   serial:"G3MG97TK1E52", udid:"00008110-000135BF380A001F", model:"iPhone 15",     platform:"iOS", os:"17.5.0", build:"21F79",   product:"iPhone 15",     battery:62, storage:"28.4 / 256 GB", ip:"192.168.1.102", mac:"b2:c3:d4:e5:f6:a1", bt:"b2:c3:d4:e5:f6:a2", owner:"admin@company.vn", email:"tranthib@company.vn",  phone:"0912345678", status:"active" },
  { id:3, name:"iPhone Lê Văn C",     serial:"H4NH08UL2F63", udid:"00008120-000246CG491B002G", model:"iPhone 13",     platform:"iOS", os:"16.7.2", build:"20H115",  product:"iPhone 13",     battery:7,  storage:"88.1 / 64 GB",  ip:"Chưa có",       mac:"c3:d4:e5:f6:a1:b2", bt:"c3:d4:e5:f6:a1:b3", owner:"admin@company.vn", email:"levanc@company.vn",    phone:"Chưa có",    status:"locked"  },
  { id:4, name:"iPhone Phạm Thị D",   serial:"J5OJ19VM3G74", udid:"00008130-000357DH502C003H", model:"iPhone 14",     platform:"iOS", os:"17.4.1", build:"21E236",  product:"iPhone 14",     battery:91, storage:"12.7 / 128 GB", ip:"192.168.1.104", mac:"d4:e5:f6:a1:b2:c3", bt:"d4:e5:f6:a1:b2:c4", owner:"admin@company.vn", email:"phamthid@company.vn",  phone:"0934567890", status:"active"  },
  { id:5, name:"TEST MDM",            serial:"F2LF86SJ0041", udid:"00008101-000024AE269A001F", model:"iPhone",        platform:"other",os:"26.4.1",build:"24E5255f", product:"iPhone 15 Pro", battery:33, storage:"16.2 / 128 GB", ip:"Chưa có",       mac:"b4:85:e1:f3:8c:a3", bt:"b4:85:e1:f3:be:94", owner:"chinhhoang@mdmcenter.vn", email:"mhgsl91@gmail.com", phone:"Chưa có", status:"active" },
  { id:6, name:"iPhone Hoàng Văn E",  serial:"K6PK20WN4H85", udid:"00008140-000468EJ613D004I", model:"iPhone 12",     platform:"iOS", os:"16.7.2", build:"20H115",  product:"iPhone 12",     battery:7,  storage:"55.3 / 64 GB",  ip:"192.168.1.106", mac:"e5:f6:a1:b2:c3:d4", bt:"e5:f6:a1:b2:c3:d5", owner:"admin@company.vn", email:"hoangvane@company.vn", phone:"0956789012", status:"locked"  },
  { id:7, name:"iPhone Ngô Thị F",    serial:"L7QL31XO5I96", udid:"00008150-000579FK724E005J", model:"iPhone 15 Pro", platform:"iOS", os:"17.5.0", build:"21F79",   product:"iPhone 15 Pro", battery:78, storage:"102.5 / 512 GB",ip:"192.168.1.107", mac:"f6:a1:b2:c3:d4:e5", bt:"f6:a1:b2:c3:d4:e6", owner:"admin@company.vn", email:"ngothif@company.vn",   phone:"0967890123", status:"active"  },
  { id:8, name:"iPhone Đặng Văn G",   serial:"M8RM42YP6J07", udid:"00008160-000680GL835F006K", model:"iPhone 11",     platform:"iOS", os:"16.7.2", build:"20H115",  product:"iPhone 11",     battery:45, storage:"38.9 / 64 GB",  ip:"Chưa có",       mac:"a2:b3:c4:d5:e6:f7", bt:"a2:b3:c4:d5:e6:f8", owner:"admin@company.vn", email:"dangvang@company.vn",  phone:"0978901234", status:"offline" },
];
